import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import PropertyAddUserMutation from 'src/mutations/PropertyAddUserMutation';
import FlashesStore from 'src/stores/FlashesStore';
import { USER_TYPE_CUSTOMER } from 'src/util/constants';
import getNodesFromEdges from 'src/util/graphql';
import isActive from 'src/util/isActive';

import AdminPropertyUserAddForm from './AdminPropertyUserAddForm';

const AdminPropertyUserAddFragment = graphql`
fragment AdminPropertyUserAdd_viewer on Viewer
@refetchable(queryName: "AdminPropertyUserAddRefetchQuery")
@argumentDefinitions(
cursor: { type: "String" },
count: { type: "Int" },
) {
    id
    viewerUser {
      email
      givenName
      familyName
    }
    properties(first: $count,after: $cursor,) 
    @connection(key: "AdminPropertyUserAdd_properties") {
      edges {
        cursor
        node {
          id
          title
          address { line1 line2 city state postcode }
          active {start finish}
        }
      }
      count
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
    users(first: $count,after: $cursor,) {
      edges {
        cursor
        node {
          id
          type
          givenName
          familyName
          email
          active {start finish}
        }
      }
      count
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

/**
 * AdminPropertyUserAdd component
 * @param {object} props
 * @param {{push: Function}} props.router - Router object used to navigate
 * to the property users page
 * @param {object} props.viewer - Viewer object containing properties and users data
 * @returns {React.ReactComponentElement} - AdminPropertyUserAdd component
 */
function AdminPropertyUserAdd(props) {
  if (!props || Object.keys(props)?.length === 0) {
    return <Loading />;
  }
  const { router, viewer } = props;
  const intl = useIntl();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const {
    data, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(
    AdminPropertyUserAddFragment,
    viewer,
  );

  const mutationName = 'propertyAddUser';

  const handleAdminPropertyUserAddSuccess = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      setError(errors[0]);
      errors.forEach((err) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [err],
        },
      }, mutationName));
      return;
    }

    const { id } = response.propertyAddUser;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      intl.formatMessage(
        { id: 'admin.admin_property_user.admin_property_user_add.response_message.success', defaultMessage: 'Successfully added Property User {uuidWithLink}' },
        {
          uuidWithLink: (
            <UUID uuid={id} url={`/admin/property-users/${id}`} />
          ),
        },
      ),
      mutationName,
    );

    router.push('/admin/property-users/');
  };

  const handleAdminPropertyUserAddFailure = (err) => {
    setProcessing(false);
    setError(err);

    FlashesStore.flash(FlashesStore.ERROR, err, mutationName);
  };

  const handleAdminPropertyUserAdd = (propertyId, userId) => {
    setProcessing(true);
    setError(null);
    FlashesStore.reset();

    PropertyAddUserMutation(
      propertyId,
      userId,
      handleAdminPropertyUserAddSuccess,
      handleAdminPropertyUserAddFailure,
    );
  };

  // Error messages
  const errorMessage = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.error.message', defaultMessage: 'Error!' },
  );

  if (error) {
    return <div>{errorMessage}</div>;
  }

  const propertyNodes = getNodesFromEdges(data?.properties);
  const activeProperties = propertyNodes.filter((property) => isActive(property?.active));
  const userNodes = getNodesFromEdges(data?.users);
  const activeUsers = userNodes.filter((user) => isActive(user?.active));
  const customerUsersList = activeUsers.filter((user) => user.type === USER_TYPE_CUSTOMER);

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.page.title', defaultMessage: 'Enosi - Admin - Property Users - Add Property User' },
  );
  const breadcrumbAdmin = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.breadcrumbs.admin.label', defaultMessage: 'Admin' },
  );
  const breadcrumbPropertyUsers = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.breadcrumbs.property_users.label', defaultMessage: 'Property Users' },
  );
  const breadcrumbAddPropertyUser = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.breadcrumbs.add_property_user.label', defaultMessage: 'Add Property User' },
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPropertyUsers, path: '/admin/property-users' },
          { name: breadcrumbAddPropertyUser },
        ]}
      />

      <AdminPropertyUserAddForm
        handleAdd={handleAdminPropertyUserAdd}
        processing={processing}
        properties={activeProperties}
        users={customerUsersList}
        loadNext={loadNext}
        hasNext={hasNext}
        isLoadingNext={isLoadingNext}
      />
    </>
  );
}

AdminPropertyUserAdd.propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default AdminPropertyUserAdd;
